import React, { useState, useCallback, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@/components/modal/Modal';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';
import { ModalProps } from '@/components/modal/useModal';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { TextField } from '@/components/form/fields/text/TextInput';
import { EmailField } from '@/components/form/fields/text/EmailInput';
import { DateField } from '@/components/form/fields/date/DateInput';
import { YesNoField } from '@/components/form/fields/yesNo/YesNoInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { CopyFromUserAction } from '@/components/form/fields/CopyFromUserAction';
import { SmartUserSelector } from '@/components/form/fields/SmartUserSelector';
import { AddressBookSelector } from '@/components/form/fields/address/AddressBookSelector';
import { AddressRequest } from '@/services/api/contracts/address';
import { Card } from '@/components/sections/Card';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useCreateCustomerAddress } from '@/services/api/hooks/address';
import { useUser } from '@/services/authentication/hooks/user';
import { useCompany, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { toast } from 'sonner';
import { parseCollectedItemString } from '@/utils/serviceRequests';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import { formatCurrency } from '@/utils/currency';

const CollectionFormSchema = z.object({
  userName: z.string().min(1, 'Required'),
  userEmail: z.string().email('Invalid email').or(z.literal('na')),
  noCorporateEmail: z.boolean().default(false),
  targetDate: z.date(),
  personalEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  userMobile: z.string().optional().or(z.literal('')),
  deliveryContactName: z.string().min(1, 'Required'),
  deliveryContactEmail: z.string().email('Invalid email'),
  deliveryContactMobile: z.string().min(1, 'Required'),
  customerReference: z.string().optional(),
  notes: z.string().optional(),
  address: AddressRequest.omit({ name: true }).extend({
    name: z.string().optional(),
    saveToAddressBook: z.boolean().optional()
  })
});

type CollectionFormFields = z.infer<typeof CollectionFormSchema>;

interface OffboardUserCollectionModalProps extends ModalProps {
  mspId: string;
  customerId: string;
  customerName: string;
  hardwareSelections: Record<string, number>;
  inferredServices: Array<{ rateCard: any; quantity: number; labels: string[] }>;
  runningTotal: number;
  devicesToBeCollected: string[];
  userName?: string;
  userEmail?: string;
  personalEmail?: string;
  userMobile?: string;
  noCorporateEmail?: boolean;
  uniqueUsers: Array<{ name: string; email: string | null; rawValue: string }>;
  onSuccessCallback: () => void;
}

export const OffboardUserCollectionModal: React.FC<OffboardUserCollectionModalProps> = ({ 
  mspId, 
  customerId, 
  customerName, 
  hardwareSelections, 
  inferredServices, 
  runningTotal, 
  devicesToBeCollected, 
  userName,
  userEmail,
  personalEmail,
  userMobile,
  noCorporateEmail,
  uniqueUsers,
  onSuccessCallback,
  ...props 
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const createServiceRequest = useCreateServiceRequest();
  const createCustomerAddress = useCreateCustomerAddress();
  const user = useUser();
  const [partner] = useMspDetailsForCustomer(mspId, customerId);
  const [company] = useCompany(mspId, customerId);

  const methods = useForm<CollectionFormFields>({
    resolver: zodResolver(CollectionFormSchema),
    defaultValues: {
      userName: userName || '',
      userEmail: userEmail || '',
      noCorporateEmail: noCorporateEmail || false,
      personalEmail: personalEmail || '',
      userMobile: userMobile || '',
      deliveryContactName: '',
      deliveryContactEmail: '',
      deliveryContactMobile: '',
      customerReference: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (props.isOpen) {
      methods.reset({
        userName: userName || '',
        userEmail: userEmail || '',
        noCorporateEmail: noCorporateEmail || false,
        personalEmail: personalEmail || '',
        userMobile: userMobile || '',
        deliveryContactName: '',
        deliveryContactEmail: '',
        deliveryContactMobile: '',
        customerReference: '',
        notes: ''
      });
    }
  }, [props.isOpen, userName, userEmail, personalEmail, userMobile, noCorporateEmail, methods]);

  const noCorporateEmailWatcher = methods.watch('noCorporateEmail');

  useEffect(() => {
    if (noCorporateEmailWatcher) {
      methods.setValue('userEmail', 'na', { shouldValidate: true, shouldDirty: false });
    } else if (methods.getValues('userEmail') === 'na') {
      methods.setValue('userEmail', '', { shouldValidate: true, shouldDirty: false });
    }
  }, [noCorporateEmailWatcher, methods]);

  const handleAddressChange = useCallback((addressPayload: any) => {
    methods.setValue('address', addressPayload, { shouldValidate: true, shouldDirty: true });
  }, [methods]);

  const onSubmit = async (data: CollectionFormFields) => {
    setErrorMessage('');
    
    let extraNotes = '';
    if (data.deliveryContactName && data.deliveryContactName !== data.userName) {
      extraNotes += `Alternative Contact: ${data.deliveryContactName}\n`;
    }
    if (data.customerReference) {
      extraNotes += `Billing Ref: ${data.customerReference}\n`;
    }
    const finalNotes = data.notes ? `${extraNotes}\n${data.notes}` : extraNotes.trim();

    const collectionAddress = [data.address.line1, data.address.line2, data.address.city, data.address.state, data.address.postcode].filter(Boolean).join(', ');

    const finalCustomerReference = data.customerReference?.trim() || data.userName || 'No Reference Provided';

    // Clean address of any leaked ids
    const { companyId, partnerId, saveToAddressBook, ...cleanAddress } = data.address as any;
    
    let finalAddress = { ...cleanAddress };

    if (saveToAddressBook) {
      try {
        const savedAddr = await createCustomerAddress.mutateAsync({
          ...cleanAddress,
          name: cleanAddress.name || 'Saved Address',
          mspId,
          customerId
        });
        const { id, companyId: cid, partnerId: pid, ...restSaved } = savedAddr as any;
        finalAddress = restSaved;
      } catch (e) {
        toast.error('Failed to save address to address book. Continuing with request...');
      }
    }

    const resolvedAddressName = finalAddress.name || 'Collection Address';

    const finalPayload = {
      requestContext: {
        requestType: 'OffboardUser',
        requestedBy: user?.email || 'Unknown',
        mspId,
        mspName: partner?.name || '',
        customerId,
        customerName: company?.name || '',
        raisedDateTime: new Date().toISOString(),
        customerReference: finalCustomerReference,
        submissionSource: 'DaaS Portal UI',
        haloClientId: company?.haloClientId || null,
        haloGeneralUserId: company?.haloGeneralUserId || null
      },
      financialContext: {
        totalEstimatedCost: Math.round(runningTotal * 100),
        totalCost: Math.round(runningTotal * 100),
        inferredServices: inferredServices.map((s: any) => ({
          rateCardId: s.rateCard?.id,
          name: s.name || s.rateCard?.name,
          quantity: s.quantity,
          price: Math.round((s.rateCard?.price?.price || 0) * 100)
        }))
      },
      userContext: {
        userName: data.userName,
        userEmail: data.userEmail,
        personalEmail: data.personalEmail,
        userMobile: data.userMobile,
        noCorporateEmail: data.noCorporateEmail
      },
      deployment: {
        isRequired: false,
        items: []
      },
      collection: {
        isRequired: true,
        logistics: {
          schedule: {
            requestedDate: data.targetDate ? new Date(data.targetDate).toISOString() : null,
            isAsapExpedite: false
          },
          address: { ...finalAddress, name: resolvedAddressName },
          siteContact: {
            contactName: data.deliveryContactName,
            contactEmail: data.deliveryContactEmail,
            contactMobile: data.deliveryContactMobile,
          },
          notes: finalNotes
        },
        items: [
          ...devicesToBeCollected.map((d: string) => {
            const parsed = parseCollectedItemString(d, 1);
            if (inferredServices.some((s: any) => 
              s.associatedPhysicalItems?.some((pi: any) => pi.type === parsed.category && pi.hasLifecyclePack)
            )) {
              parsed.warranty.lifecyclePack = "Yes";
            }
            return parsed;
          })
        ]
      }
    };

    try {
      await createServiceRequest.mutateAsync({
        params: { mspId, customerId },
        body: {
          requestedBy: user?.email || 'Unknown',
          requestType: 'OffboardUser',
          customerReference: finalCustomerReference,
          payload: finalPayload
        }
      });

      toast.success('Service Request Logged Successfully');
      onSuccessCallback();
      props.close();
    } catch (e) {
      toast.error('Failed to log request. Please try again.');
    }
  };

  const { isDirty } = methods.formState;
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  useDirtyFormGuard(isDirty);

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      props.close();
    }
  };

  const guardedProps = { ...props, close: handleClose };

  const isLoading = createServiceRequest.isLoading;

  return (
    <>
      <Modal className="md:max-w-5xl lg:max-w-6xl flex flex-col h-[90vh]" {...guardedProps}>
      <Card className="w-full h-full flex flex-col overflow-hidden bg-white">
        <div className="p-8 pb-4 shrink-0">
          <Modal.Controls {...guardedProps}>
            <h1 className="text-2xl font-semibold">Offboard User</h1>
          </Modal.Controls>
          <p className="text-gray-600">Please provide the logistical details for this user offboard.</p>
        </div>
        
        <Form methods={methods} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column: The Receipt & Admin Details */}
            <div className="space-y-6">
              <div className="flex flex-col border border-gray-200 rounded bg-gray-50 p-6 h-fit">
                <h3 className="text-lg font-bold border-b pb-3 mb-4">Collection Summary</h3>
                
                {devicesToBeCollected && devicesToBeCollected.length > 0 && (
                  <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                    <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Collect:</p>
                    <ul className="space-y-1 text-gray-600 list-disc list-inside">
                      {devicesToBeCollected.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-4 flex-1 mt-4">

                  {inferredServices.map((item: any, index: number) => {
                    const isInventoryItem = item.isInventoryItem;
                    return (
                      <div key={index} className="flex justify-between items-start text-sm border-b border-gray-100 last:border-b-0 pb-2">
                        <div className="pr-4">
                          <span className="font-medium">{item.quantity}x</span> {item.name || item.rateCard?.name || 'Service'}
                          {item.hasLifecyclePack && (
                            <span className="italic text-green-600 text-xs block mt-1">
                              Lifecycle Pack Detected<br />Costs will be adjusted accordingly
                            </span>
                          )}
                        </div>
                        <div className="font-medium whitespace-nowrap">
                          {isInventoryItem ? (
                            null
                          ) : (
                            formatCurrency(((item.rateCard?.price?.price || 0) / 100) * item.quantity, company?.region?.code)
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {inferredServices.length === 0 && (
                    <div className="text-gray-500 italic text-sm">No items selected.</div>
                  )}
                </div>
                
                <div className="flex justify-between items-center pt-4 mt-6 border-t font-bold text-lg">
                  <span>Estimated Total</span>
                  <span>{formatCurrency(runningTotal || 0, company?.region?.code)}</span>
                </div>
              </div>

              <div className="border p-4 rounded space-y-4">
                  <div>
                    <TextField id="customerReference" label="Customer/MSP Reference" placeholder="This will appear on the invoice" optional={true} />
                    <p className="text-xs text-gray-500 italic mt-1">If no reference is given, the user's name will be used</p>
                  </div>
              </div>

              <div className="border p-4 rounded space-y-4">
                <TextAreaField id="notes" label="Notes / Special Requirements" optional={true} />
              </div>
            </div>

          {/* Right Column: Logistics & Data Entry */}
          <div className="space-y-6">
              <div className="border p-4 rounded space-y-4">
                <h4 className="text-lg font-bold">User Details</h4>
                <div className="space-y-4">
                  <TextField id="userName" label="User Name" />
                  <div className="space-y-4">
                    <div className={methods.watch('noCorporateEmail') ? 'opacity-50 pointer-events-none' : ''}>
                      <EmailField 
                        id="userEmail" 
                        label="Corporate Email" 
                        disabled={methods.watch('noCorporateEmail')}
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
                      <input type="checkbox" {...methods.register('noCorporateEmail')} className="w-4 h-4 rounded border-gray-300 text-primary-700 disabled:opacity-50" />
                      User does not have corporate email
                    </label>
                    <EmailField id="personalEmail" label="Forwarding Personal Email" optional={true} />
                    <TextField id="userMobile" label="Personal Mobile Number" />
                  </div>
                </div>
              </div>

              <SmartUserSelector 
                methods={methods as any} 
                uniqueUsers={uniqueUsers} 
                onSelect={() => {}} 
                isSimpleContactMode={true}
                title="Collection Contact"
                headerAction={
                  <CopyFromUserAction 
                    control={methods.control} 
                    setValue={methods.setValue} 
                    getValues={methods.getValues} 
                    sourceNameField="userName" 
                    sourcePrimaryEmailField="userEmail" 
                    sourceFallbackEmailField="personalEmail" 
                    sourceMobileField="userMobile" 
                    targetNameField="deliveryContactName" 
                    targetEmailField="deliveryContactEmail" 
                    targetMobileField="deliveryContactMobile" 
                  />
                }
                nameField="deliveryContactName"
                corpEmailField="deliveryContactEmail"
                mobileNumberField="deliveryContactMobile"
                nameLabel="Contact Name"
                corpEmailLabel="Contact Email"
                mobileNumberLabel="Contact Mobile"
                personalEmailOptional={false}
                mobileNumberOptional={false}
              />

              <div className="border p-4 rounded space-y-4">
                <h4 className="text-lg font-bold">Collection Address</h4>
                <AddressBookSelector 
                  mspId={mspId}
                  customerId={customerId}
                  onChange={handleAddressChange}
                />
                {methods.formState.errors.address && (
                  <p className="text-red-500 text-sm mt-1">Please select or provide a valid address.</p>
                )}
                <DateField
                  id="targetDate"
                  label="User Termination Date"
                  excludeDates={[new Date().toDateString()]}
                  excludePastDates={true}
                />
              </div>

              {errorMessage && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded border border-red-200">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50 shrink-0">
            <Button variant={ButtonVariant.Ghost} onClick={guardedProps.close} className="mr-4" disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant={ButtonVariant.Primary}
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Confirm & Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </Modal>
    <UnsavedChangesModal
      isOpen={showUnsavedWarning}
      open={() => setShowUnsavedWarning(true)}
      close={() => setShowUnsavedWarning(false)}
      onCancelCallback={() => setShowUnsavedWarning(false)}
      onConfirmCallback={() => {
        setShowUnsavedWarning(false);
        props.close();
      }}
      title="Abandon Request"
      message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
      confirmText="Yes, Abandon"
      confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
      cancelText="No, Continue"
      cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
    />
    </>
  );
};
